import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useDataMode } from '../../contexts/DataModeContext';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  FileText, Plus, Search, Eye, Clock,
  Building2, DollarSign, ChevronLeft, ChevronRight, Download,
  Send, Target, LayoutTemplate, UserPlus, X, Loader2, CheckCircle2,
  Car, Monitor, Heart, ShoppingBag, Gem, Wrench, RefreshCw, Rocket,
  Zap, Star, PartyPopper
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Deadline countdown helper
const getDeadlineInfo = (deadlineStr) => {
  if (!deadlineStr) return null;
  try {
    const dl   = new Date(deadlineStr);
    const diff = Math.ceil((dl - new Date()) / 86400000);
    if (diff < 0)   return { label: 'Süresi Doldu',     color: 'text-gray-400',  bg: 'bg-gray-50',   urgent: false };
    if (diff === 0) return { label: 'Bugün Son!',        color: 'text-red-600',   bg: 'bg-red-50',    urgent: true  };
    if (diff <= 3)  return { label: `${diff} gün kaldı`, color: 'text-red-600',   bg: 'bg-red-50',    urgent: true  };
    if (diff <= 7)  return { label: `${diff} gün kaldı`, color: 'text-amber-600', bg: 'bg-amber-50',  urgent: false };
    return               { label: `${diff} gün kaldı`,   color: 'text-gray-500',  bg: 'bg-gray-50',   urgent: false };
  } catch { return null; }
};

const TEMPLATE_ICONS = { Car, Monitor, Heart, ShoppingBag, Diamond: Gem, Building2, RefreshCw, Wrench };
const tIcon = (name) => { const I = TEMPLATE_ICONS[name]; return I ? <I className="w-5 h-5" /> : <LayoutTemplate className="w-5 h-5" />; };

const MOCK_BRIEFS = [
  { id: 'br_001', title: 'Istanbul Tech Fair 2025 — Stand Design', status: 'published', proposals_count: 4, budget_min: 35000, budget_max: 45000, currency: 'TRY', deadline: '2025-02-15', city: 'Istanbul', booth_size: '6x4', created_at: '2025-01-03' },
  { id: 'br_002', title: 'Hannover Messe 2025 — Modular Stand', status: 'awarded', proposals_count: 7, budget_min: 70000, budget_max: 82000, currency: 'EUR', deadline: '2025-03-01', city: 'Hannover', booth_size: '9x6', created_at: '2024-12-20' },
  { id: 'br_003', title: 'Dubai Expo Corporate Pavilion', status: 'draft', proposals_count: 0, budget_min: 50000, budget_max: 58000, currency: 'USD', deadline: '2025-04-10', city: 'Dubai', booth_size: '12x6', created_at: '2025-01-09' },
  { id: 'br_004', title: 'Automechanika Frankfurt 2025', status: 'closed', proposals_count: 5, budget_min: 90000, budget_max: 110000, currency: 'EUR', deadline: '2025-01-20', city: 'Frankfurt', booth_size: '18x9', created_at: '2024-11-15' },
  { id: 'br_005', title: 'IzFas Organic Food Stand', status: 'published', proposals_count: 2, budget_min: 15000, budget_max: 20000, currency: 'TRY', deadline: '2025-02-28', city: 'Izmir', booth_size: '3x3', created_at: '2025-01-05' },
  { id: 'br_006', title: 'Mobile World Congress 2025', status: 'published', proposals_count: 9, budget_min: 120000, budget_max: 150000, currency: 'EUR', deadline: '2025-02-10', city: 'Barcelona', booth_size: '15x10', created_at: '2024-12-28' },
  { id: 'br_007', title: 'Construction & Design Center', status: 'draft', proposals_count: 0, budget_min: 25000, budget_max: 35000, currency: 'TRY', deadline: '2025-03-15', city: 'Ankara', booth_size: '6x6', created_at: '2025-01-10' },
  { id: 'br_008', title: 'Arab Health Dubai 2025', status: 'awarded', proposals_count: 3, budget_min: 80000, budget_max: 95000, currency: 'USD', deadline: '2025-01-28', city: 'Dubai', booth_size: '9x9', created_at: '2024-12-01' },
];

const STATUS_CONFIG = {
  draft:     { color: 'bg-gray-100 text-gray-600',       labelKey: 'briefs.draft' },
  published: { color: 'bg-blue-100 text-blue-700',       labelKey: 'briefs.published' },
  awarded:   { color: 'bg-emerald-100 text-emerald-700', labelKey: 'briefs.awarded' },
  closed:    { color: 'bg-red-100 text-red-600',         labelKey: 'briefs.closed' },
};

const PAGE_SIZE = 6;

const EMPTY_FORM = {
  show: '', country: 'DE', city: '', month: '',
  width: '', depth: '', budget_min: '', budget_max: '', currency: 'EUR',
  deadline: '', notes: '',
};

const CURRENCIES = ['EUR', 'USD', 'GBP', 'AED', 'TRY'];
const COUNTRY_OPTIONS = [
  ['DE', 'Germany'], ['TR', 'Türkiye'], ['AE', 'UAE'],
  ['ES', 'Spain'], ['NL', 'Netherlands'], ['FR', 'France'],
  ['GB', 'United Kingdom'], ['US', 'USA'], ['SG', 'Singapore'], ['IT', 'Italy'],
];

const NewBriefModal = ({ onClose, onCreated, token, isMockMode }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      const payload = {
        event: { country: form.country, city: form.city, show: form.show, month: form.month || `${new Date().getFullYear()}-01` },
        stand: { w_m: parseFloat(form.width) || 3, d_m: parseFloat(form.depth) || 3 },
        budget: { min: parseFloat(form.budget_min) || 0, max: parseFloat(form.budget_max) || 0, currency: form.currency },
        deadline_at: form.deadline || new Date(Date.now() + 30 * 86400000).toISOString().split('T')[0],
        notes: form.notes || null,
      };

      if (isMockMode) {
        await new Promise(r => setTimeout(r, 700));
        const newBrief = {
          id: `br_${Date.now()}`, title: `${form.show} — Stand Design`,
          status: 'draft', proposals_count: 0,
          budget_min: payload.budget.min, budget_max: payload.budget.max,
          currency: form.currency, deadline: form.deadline,
          city: form.city, booth_size: `${form.width}x${form.depth}`,
          created_at: new Date().toISOString().split('T')[0],
        };
        onCreated(newBrief);
      } else {
        const res = await axios.post(`${API}/exhibitor/briefs`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        onCreated({ id: res.data.brief_id, title: `${form.show} — Stand Design`, status: 'draft', proposals_count: 0, budget_min: payload.budget.min, budget_max: payload.budget.max, currency: form.currency, deadline: form.deadline, city: form.city, booth_size: `${form.width}x${form.depth}`, created_at: new Date().toISOString().split('T')[0] });
      }
      setDone(true);
      setTimeout(onClose, 1500);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to create brief. Please check all fields.');
    } finally { setLoading(false); }
  };

  if (done) return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-10 text-center max-w-sm w-full">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-emerald-600" />
        </div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Brief Created!</h3>
        <p className="text-gray-500 text-sm">Your brief has been saved as a draft. Publish it to start receiving quotes.</p>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">Create New Brief</h3>
            <p className="text-xs text-gray-400">Fill in your stand requirements to receive quotes</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center">
            <X className="w-4 h-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Event Info */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Event Information</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Show / Fair Name *</label>
                <Input value={form.show} onChange={e => set('show', e.target.value)} placeholder="e.g. Hannover Messe 2025" required className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Country *</label>
                <select value={form.country} onChange={e => set('country', e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500">
                  {COUNTRY_OPTIONS.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">City *</label>
                <Input value={form.city} onChange={e => set('city', e.target.value)} placeholder="e.g. Hannover" required className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Event Month (YYYY-MM)</label>
                <Input value={form.month} onChange={e => set('month', e.target.value)} placeholder="2025-04" pattern="\d{4}-\d{2}" className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Submission Deadline</label>
                <Input type="date" value={form.deadline} onChange={e => set('deadline', e.target.value)} className="h-10" />
              </div>
            </div>
          </div>

          {/* Stand Size */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Stand Size</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Width (m) *</label>
                <Input type="number" min="1" step="0.5" value={form.width} onChange={e => set('width', e.target.value)} placeholder="6" required className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Depth (m) *</label>
                <Input type="number" min="1" step="0.5" value={form.depth} onChange={e => set('depth', e.target.value)} placeholder="4" required className="h-10" />
              </div>
              {form.width && form.depth && (
                <div className="col-span-2 bg-orange-50 rounded-lg px-3 py-2 text-sm text-orange-700 font-medium">
                  Area: {(parseFloat(form.width) * parseFloat(form.depth)).toFixed(0)} sqm
                </div>
              )}
            </div>
          </div>

          {/* Budget */}
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Budget</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Min Budget *</label>
                <Input type="number" min="0" value={form.budget_min} onChange={e => set('budget_min', e.target.value)} placeholder="10000" required className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Max Budget *</label>
                <Input type="number" min="0" value={form.budget_max} onChange={e => set('budget_max', e.target.value)} placeholder="15000" required className="h-10" />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 mb-1 block">Currency</label>
                <select value={form.currency} onChange={e => set('currency', e.target.value)}
                  className="w-full h-10 px-3 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500">
                  {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">Additional Notes</label>
            <Textarea value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Design preferences, special requirements, branding guidelines..." rows={3} />
          </div>

          {error && <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-700">{error}</div>}

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1 h-11">Cancel</Button>
            <Button type="submit" disabled={loading} className="flex-1 h-11 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
              {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Create Brief
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Template Modal ────────────────────────────────────────────────────────────
const TemplateModal = ({ token, onClose, onSelect }) => {
  const [templates, setTemplates] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    axios.get(`${API}/exhibitor/brief-templates`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setTemplates(r.data))
      .catch(() => setTemplates({ predefined: BRIEF_TEMPLATES_FALLBACK, my_templates: [] }))
      .finally(() => setLoading(false));
  }, [token]);

  const allTemplates = templates ? [...(templates.predefined || []), ...(templates.my_templates || [])] : [];
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><LayoutTemplate className="w-5 h-5 text-orange-500" />Brief Templates</h3>
            <p className="text-xs text-gray-500 mt-0.5">Select a template to pre-fill your brief</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {loading ? [...Array(6)].map((_,i) => <div key={i} className="h-24 bg-gray-100 animate-pulse rounded-xl" />) :
           allTemplates.map(t => (
            <button key={t.id} onClick={() => onSelect(t)} data-testid={`template-${t.id}`}
              className="text-left p-4 rounded-xl border border-gray-200 hover:border-orange-300 hover:bg-orange-50/50 transition-all group">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-9 h-9 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">{tIcon(t.icon)}</div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{t.name}</p>
                  <p className="text-[10px] text-orange-500 font-semibold uppercase tracking-wide">{t.category}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <span>{t.city}, {t.country}</span>
                <span>•</span><span>{t.w_m}×{t.d_m}m</span>
                <span>•</span><span className="font-semibold text-gray-700">{(t.budget_max/1000).toFixed(0)}K {t.currency}</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// ── Invite Contractor Modal ───────────────────────────────────────────────────
const InviteContractorModal = ({ token, briefId, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [invited, setInvited] = useState(new Set());
  const [inviting, setInviting] = useState(null);

  const search = async (q) => {
    setSearching(true);
    try {
      const res = await axios.get(`${API}/exhibitor/contractor-search`, {
        headers: { Authorization: `Bearer ${token}` }, params: { q }
      });
      setResults(res.data?.items || []);
    } catch { setResults([]); } finally { setSearching(false); }
  };

  useEffect(() => { search(''); }, []);
  useEffect(() => {
    const t = setTimeout(() => search(query), 400);
    return () => clearTimeout(t);
  }, [query]);

  const handleInvite = async (org) => {
    setInviting(org.id);
    try {
      await axios.post(`${API}/exhibitor/briefs/${briefId}/invite`,
        { contractor_org_id: org.id, message: '' },
        { headers: { Authorization: `Bearer ${token}` } });
      setInvited(prev => new Set([...prev, org.id]));
    } catch (e) {
      if (e.response?.status === 409) setInvited(prev => new Set([...prev, org.id]));
    } finally { setInviting(null); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2"><UserPlus className="w-5 h-5 text-blue-500" />Invite Contractor</h3>
            <p className="text-xs text-gray-500 mt-0.5">Privately invite contractors to quote for this brief</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}><X className="w-4 h-4" /></Button>
        </div>
        <div className="p-5 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search contractors by name..." className="pl-9"
              value={query} onChange={e => setQuery(e.target.value)} />
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {searching ? <p className="text-center py-6 text-sm text-gray-400">Searching...</p> :
             results.length === 0 ? <p className="text-center py-6 text-sm text-gray-400">No contractors found</p> :
             results.map(org => {
               const done = invited.has(org.id);
               return (
                 <div key={org.id} className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:bg-gray-50">
                   <div className="min-w-0">
                     <p className="text-sm font-semibold text-gray-800 truncate">{org.display_name}</p>
                     <p className="text-xs text-gray-400">{org.city}, {org.country}</p>
                     {org.services?.length > 0 && (
                       <div className="flex gap-1 mt-1 flex-wrap">
                         {org.services.map((s,i) => <span key={i} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{s}</span>)}
                       </div>
                     )}
                   </div>
                   <Button size="sm" disabled={done || inviting === org.id}
                     className={`ml-3 flex-shrink-0 gap-1 text-xs ${done ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                     onClick={() => !done && handleInvite(org)} data-testid={`invite-contractor-${org.id}`}>
                     {done ? <><CheckCircle2 className="w-3 h-3" />Invited</> :
                      inviting === org.id ? <><Loader2 className="w-3 h-3 animate-spin" />...</> :
                      <><Send className="w-3 h-3" />Invite</>}
                   </Button>
                 </div>
               );
             })}
          </div>
          {invited.size > 0 && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-center">
              <p className="text-sm text-emerald-700 font-semibold">{invited.size} contractor{invited.size > 1 ? 's' : ''} invited!</p>
              <p className="text-xs text-emerald-600 mt-0.5">They will receive a notification to submit a quote.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Brief Boost Modal ─────────────────────────────────────────────────────────
const BoostModal = ({ brief, onClose, token }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleBoost = async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(
        `${API}/exhibitor/briefs/${brief.id}/boost`,
        { origin_url: window.location.origin },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'İşlem başarısız. Lütfen tekrar deneyin.');
      setLoading(false);
    }
  };

  const isBoosted = brief._raw?.is_boosted;
  const boostExpires = brief._raw?.boost_expires_at;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 rounded-t-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-white/20 rounded-full">
              <Rocket className="h-5 w-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Brief Boost</h3>
              <p className="text-xs opacity-90">Öne çıkan listede görün</p>
            </div>
          </div>
          <p className="text-sm opacity-90 mt-2 line-clamp-2">{brief.title}</p>
        </div>

        <div className="p-6 space-y-4">
          {isBoosted && boostExpires ? (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
              <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
              <p className="text-sm font-bold text-green-800">Bu brief zaten boosted!</p>
              <p className="text-xs text-green-600 mt-1">
                Süre: {new Date(boostExpires).toLocaleDateString('tr-TR')} tarihine kadar
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {[
                  { icon: Star, text: 'Brief Board\'da en üste çıkar', sub: 'Contractor\'lar ilk seni görür' },
                  { icon: Zap, text: 'Özel "ÖNE ÇIKAN" rozeti', sub: 'Dikkat çekici altın rozet' },
                  { icon: Rocket, text: '7 gün süre', sub: '1 haftanızı en verimli kullanın' },
                ].map(item => (
                  <div key={item.text} className="flex items-start gap-3">
                    <div className="p-1.5 rounded-lg bg-orange-100">
                      <item.icon className="h-4 w-4 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{item.text}</p>
                      <p className="text-xs text-gray-500">{item.sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">7 günlük boost</p>
                  <p className="text-2xl font-black text-orange-600">€29</p>
                </div>
                <p className="text-xs text-gray-500 text-right">Tek seferlik<br />ödeme</p>
              </div>

              {error && (
                <p className="text-xs text-red-600 bg-red-50 rounded-lg p-2">{error}</p>
              )}
            </>
          )}

          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose} className="flex-1">İptal</Button>
            {!isBoosted && (
              <Button
                onClick={handleBoost}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white gap-2"
                data-testid="boost-pay-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
                {loading ? 'Yönlendiriliyor...' : 'Ödemeye Geç'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const BRIEF_TEMPLATES_FALLBACK = [
  { id: 'tmpl_automotive', name: 'Automotive', icon: 'Car', category: 'Automotive', country: 'DE', city: 'Frankfurt', w_m: 12, d_m: 9, budget_min: 80000, budget_max: 120000, currency: 'EUR', notes: '' },
  { id: 'tmpl_technology', name: 'Technology', icon: 'Monitor', category: 'Technology', country: 'DE', city: 'Hannover', w_m: 9, d_m: 6, budget_min: 50000, budget_max: 80000, currency: 'EUR', notes: '' },
  { id: 'tmpl_healthcare', name: 'Healthcare',  icon: 'Heart', category: 'Healthcare', country: 'NL', city: 'Amsterdam', w_m: 9, d_m: 6, budget_min: 70000, budget_max: 100000, currency: 'EUR', notes: '' },
];

const ExhibitorBriefs = () => {
  const { isMockMode } = useDataMode();
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toastedMilestonesRef = useRef(new Set());
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [budgetMin, setBudgetMin] = useState('');
  const [budgetMax, setBudgetMax] = useState('');
  const [quickFilter, setQuickFilter] = useState(''); // 'last_week' | 'deadline_week'
  const [page, setPage] = useState(1);
  const [selectedBrief, setSelectedBrief] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [inviteBriefId, setInviteBriefId] = useState(null);
  const [boostBriefId, setBoostBriefId] = useState(null);
  const [boostSuccess, setBoostSuccess] = useState(null);

  // Handle boost success redirect
  useEffect(() => {
    const boostId = searchParams.get('boost_success');
    if (boostId) {
      setBoostSuccess(boostId);
      // Activate boost via API
      if (token) {
        axios.post(`${API}/exhibitor/briefs/${boostId}/boost-activate`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      }
      setTimeout(() => setBoostSuccess(null), 5000);
    }
  }, [searchParams, token]);

  useEffect(() => {
    const load = async () => {
      setLoading(true); setError(null);
      if (isMockMode) {
        await new Promise(r => setTimeout(r, 500));
        setBriefs(MOCK_BRIEFS);
      } else {
        try {
          const res = await axios.get(`${API}/exhibitor/briefs`, { headers: { Authorization: `Bearer ${token}` } });
          const items = (res.data?.items || []).map(b => ({
            id: b.id,
            title: `${b.event?.show || 'Brief'} — Stand Design`,
            status: (b.status || 'DRAFT').toLowerCase().replace('open','published'),
            proposals_count: b.proposals_count || 0,
            budget_min: b.budget?.min,
            budget_max: b.budget?.max,
            currency: b.budget?.currency || 'EUR',
            deadline: b.deadline_at,
            city: b.event?.city,
            booth_size: b.stand ? `${b.stand.w_m}x${b.stand.d_m}` : '',
            created_at: b.created_at,
            notes: b.notes,
            targeted_contractor_id: b.targeted_contractor_id,
            targeted_contractor_name: b.targeted_contractor_name,
            is_boosted: b.is_boosted || false,
            boost_expires_at: b.boost_expires_at,
            interest_count: b.interest_count || 0,
            _raw: b,
          }));
          setBriefs(items);
          // Brief milestone toasts — fire once per session per brief milestone (localStorage ile spam önleme)
          const MILESTONES = [1, 3, 5, 10, 20];
          const firedKey = 'expostand_milestone_toasts';
          let fired;
          try { fired = new Set(JSON.parse(localStorage.getItem(firedKey) || '[]')); }
          catch { fired = new Set(); }
          let updated = false;
          items.forEach(b => {
            if (b.status !== 'published') return;
            const count = b.proposals_count;
            const milestone = MILESTONES.find(m => m === count);
            if (!milestone) return;
            const key = `${b.id}_${milestone}`;
            if (fired.has(key)) return;
            fired.add(key);
            updated = true;
            const messages = {
              1:  `First quote received for "${b.title.split('—')[0].trim()}"!`,
              3:  `3 contractors quoted "${b.title.split('—')[0].trim()}"!`,
              5:  `"${b.title.split('—')[0].trim()}" has 5 quotes — getting competitive!`,
              10: `"${b.title.split('—')[0].trim()}" hit 10 proposals — great traction!`,
              20: `Wow! 20 quotes on "${b.title.split('—')[0].trim()}". Time to decide!`,
            };
            toast.success(messages[milestone], { duration: 5000, icon: '🎉' });
          });
          if (updated) {
            try { localStorage.setItem(firedKey, JSON.stringify([...fired])); } catch {}
          }
        } catch { setError('Could not connect to API.'); setBriefs([]); }
      }
      setLoading(false);
    };
    load();
  }, [isMockMode, token]);

  const handlePublish = async (briefId, e) => {
    e.stopPropagation();
    try {
      if (!isMockMode) {
        await axios.patch(`${API}/exhibitor/briefs/${briefId}/status`, { status: 'OPEN' }, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setBriefs(prev => prev.map(b => b.id === briefId ? { ...b, status: 'published' } : b));
    } catch { /* ignore */ }
  };

  const filtered = briefs.filter(b => {
    const q = search.toLowerCase();
    const now = new Date();
    const matchesSearch = !q || b.title?.toLowerCase().includes(q) || b.city?.toLowerCase().includes(q);
    const matchesStatus = statusFilter === 'all' || b.status === statusFilter;
    const bMax = b.budget_max || 0;
    const matchesBudgetMin = !budgetMin || bMax >= parseInt(budgetMin, 10);
    const matchesBudgetMax = !budgetMax || bMax <= parseInt(budgetMax, 10);
    const matchesQuick = !quickFilter || (() => {
      if (quickFilter === 'last_week') return b.created_at && new Date(b.created_at) >= new Date(now - 7 * 86400000);
      if (quickFilter === 'deadline_week') return b.deadline && new Date(b.deadline) <= new Date(now.getTime() + 7 * 86400000) && new Date(b.deadline) >= now;
      return true;
    })();
    return matchesSearch && matchesStatus && matchesBudgetMin && matchesBudgetMax && matchesQuick;
  });

  const exportCSV = () => {
    if (!briefs.length) return;
    const headers = ['Title', 'Status', 'City', 'Budget Min', 'Budget Max', 'Currency', 'Deadline', 'Proposals', 'Created'];
    const rows = briefs.map(b => [
      `"${b.title || ''}"`, b.status || '', b.city || '',
      b.budget_min || '', b.budget_max || '', b.currency || 'EUR',
      b.deadline ? new Date(b.deadline).toLocaleDateString('en-GB') : '',
      b.proposals_count || 0,
      b.created_at ? new Date(b.created_at).toLocaleDateString('en-GB') : '',
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `briefs-${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
  };

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">My Briefs</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage your stand requests</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowTemplates(true)}
            data-testid="template-btn" className="gap-2 border-orange-200 text-orange-600 hover:bg-orange-50">
            <LayoutTemplate className="h-4 w-4" />Use Template
          </Button>
          <Button data-testid="create-brief-btn"
            onClick={() => navigate('/app/exhibitor/briefs/new')}
            className="bg-orange-500 hover:bg-orange-600 text-white gap-2">
            <Plus className="h-4 w-4" />{t('briefs.newBrief')}
          </Button>
        </div>
      </div>

      {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>}

      {/* Boost success banner */}
      {boostSuccess && (
        <div className="flex items-center gap-3 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-xl p-4" data-testid="boost-success-banner">
          <Rocket className="h-5 w-5 text-orange-500 shrink-0" />
          <div>
            <p className="text-sm font-bold text-orange-800">Brief başarıyla öne çıkarıldı!</p>
            <p className="text-xs text-orange-600 mt-0.5">7 gün boyunca Brief Board'da en üstte görünecek.</p>
          </div>
        </div>
      )}

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input placeholder="Search briefs..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} className="pl-9 bg-gray-50 border-gray-200" />
          </div>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
            data-testid="status-filter"
            className="px-3 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 text-gray-700 focus:outline-none focus:ring-2 focus:ring-orange-500">
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="awarded">Awarded</option>
            <option value="closed">Closed</option>
          </select>
          <div className="flex items-center gap-1.5">
            <input type="number" value={budgetMin} onChange={e => { setBudgetMin(e.target.value); setPage(1); }}
              placeholder="Min €" data-testid="brief-budget-min"
              className="w-20 px-2 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500" />
            <span className="text-gray-400 text-xs">–</span>
            <input type="number" value={budgetMax} onChange={e => { setBudgetMax(e.target.value); setPage(1); }}
              placeholder="Max €" data-testid="brief-budget-max"
              className="w-20 px-2 py-2 border border-gray-200 rounded-md text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </div>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={exportCSV} data-testid="export-briefs-csv">
            <Download className="h-3.5 w-3.5" />Export CSV
          </Button>
          <span className="text-sm text-gray-400 ml-auto">{filtered.length} briefs</span>
        </CardContent>
      </Card>
      {/* Quick Filter Chips */}
      <div className="flex gap-2 flex-wrap -mt-2">
        {[
          { key: 'last_week',     label: 'Created Last 7 Days', icon: '✨' },
          { key: 'deadline_week', label: 'Deadline This Week',  icon: '⚡' },
        ].map(({ key, label, icon }) => (
          <button key={key} onClick={() => { setQuickFilter(q => q === key ? '' : key); setPage(1); }}
            data-testid={`brief-quick-${key}`}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              quickFilter === key
                ? 'bg-orange-500 text-white border-orange-500 shadow-sm'
                : 'bg-white text-gray-600 border-gray-200 hover:border-orange-400 hover:text-orange-700'
            }`}>
            <span>{icon}</span>{label}
            {quickFilter === key && <span className="text-orange-100 ml-0.5">×</span>}
          </button>
        ))}
        {(quickFilter || statusFilter !== 'all' || budgetMin || budgetMax) && (
          <button onClick={() => { setQuickFilter(''); setStatusFilter('all'); setBudgetMin(''); setBudgetMax(''); setPage(1); }}
            className="text-xs text-gray-400 hover:text-red-500 font-medium px-2 transition-colors">
            Reset all
          </button>
        )}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => <div key={i} className="h-48 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : paged.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">{t('briefs.noBriefs')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paged.map(b => {
            const sc = STATUS_CONFIG[b.status] || STATUS_CONFIG.draft;
            return (
              <Card key={b.id} className={`border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${b.is_boosted ? 'ring-2 ring-orange-400 ring-offset-1' : ''}`} onClick={() => setSelectedBrief(b)}>
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${sc.color}`}>{t(sc.labelKey)}</span>
                      {b.is_boosted && (
                        <span className="text-xs font-bold text-white bg-gradient-to-r from-orange-500 to-amber-500 px-2 py-0.5 rounded-full flex items-center gap-1">
                          <Rocket className="h-2.5 w-2.5" />ÖNE ÇIKAN
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {b.interest_count > 0 && (
                        <span className="text-xs bg-purple-50 text-purple-600 font-semibold px-2 py-0.5 rounded-full flex items-center gap-1" title="Contractors interested">
                          <Heart className="h-3 w-3" />{b.interest_count}
                        </span>
                      )}
                      {b.proposals_count > 0 && <span className="text-xs bg-orange-50 text-orange-600 font-semibold px-2 py-1 rounded-full">{b.proposals_count} quotes</span>}
                    </div>
                  </div>
                  <h3 className="text-sm font-bold text-gray-900 leading-snug mb-3 line-clamp-2">{b.title}</h3>
                  {b.targeted_contractor_name && (
                    <div className="flex items-center gap-1.5 mb-3 bg-orange-50 border border-orange-100 rounded-lg px-2.5 py-1.5">
                      <Target className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />
                      <span className="text-xs text-orange-700 font-semibold truncate">Direct → {b.targeted_contractor_name}</span>
                    </div>
                  )}
                  <div className="space-y-1.5 text-xs text-gray-500">
                    {b.city && <div className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gray-400" />{b.city}{b.booth_size ? ` — ${b.booth_size}m` : ''}</div>}
                    {b.deadline && (() => {
                      const di = getDeadlineInfo(b.deadline);
                      return (
                        <div className="flex items-center gap-1.5">
                          <Clock className={`h-3.5 w-3.5 ${di?.urgent ? 'text-red-400' : 'text-gray-400'}`} />
                          <span className={di ? `font-semibold ${di.color}` : ''}>
                            {di?.urgent ? '⏰ ' : ''}{di ? di.label : new Date(b.deadline).toLocaleDateString('tr-TR')}
                          </span>
                          <span className="text-gray-400 font-normal">({new Date(b.deadline).toLocaleDateString('tr-TR')})</span>
                        </div>
                      );
                    })()}
                    {(b.budget_max || b.budget_min) && (
                      <div className="flex items-center gap-1.5 font-semibold text-gray-700">
                        <DollarSign className="h-3.5 w-3.5 text-gray-400" />
                        {b.budget_min?.toLocaleString()} – {b.budget_max?.toLocaleString()} {b.currency}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4 pt-3 border-t border-gray-50">
                    <Button size="sm" variant="outline" className="flex-1 h-7 text-xs gap-1" onClick={() => setSelectedBrief(b)}><Eye className="h-3 w-3" />View</Button>
                    {b.status === 'draft' && (
                      <Button size="sm" className="flex-1 h-7 text-xs gap-1 bg-orange-500 hover:bg-orange-600 text-white"
                        onClick={(e) => handlePublish(b.id, e)}>
                        <Send className="h-3 w-3" />Publish
                      </Button>
                    )}
                    {b.status === 'published' && (
                      <Button size="sm" variant="outline"
                        className="h-7 text-xs gap-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                        onClick={(e) => { e.stopPropagation(); setInviteBriefId(b.id); }}
                        data-testid={`invite-btn-${b.id}`}>
                        <UserPlus className="h-3 w-3" />Invite
                      </Button>
                    )}
                    {(b.status === 'published' || b.status === 'draft') && !b.is_boosted && (
                      <Button size="sm" variant="outline"
                        className="h-7 text-xs gap-1 border-orange-200 text-orange-600 hover:bg-orange-50"
                        onClick={(e) => { e.stopPropagation(); setBoostBriefId(b.id); }}
                        data-testid={`boost-btn-${b.id}`}>
                        <Rocket className="h-3 w-3" />Boost
                      </Button>
                    )}
                    {b.proposals_count > 0 && (
                      <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-blue-200 text-blue-600 hover:bg-blue-50"
                        data-testid={`view-quotes-btn-${b.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/app/exhibitor/proposals?brief=${b.id}`); }}>
                        {b.proposals_count} Quotes
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">{(page-1)*PAGE_SIZE+1}–{Math.min(page*PAGE_SIZE,filtered.length)} / {filtered.length}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page===1} onClick={()=>setPage(p=>p-1)}><ChevronLeft className="h-4 w-4"/></Button>
            {[...Array(totalPages)].map((_,i)=><Button key={i} variant={page===i+1?'default':'outline'} size="sm" className="h-7 w-7 p-0 text-xs" onClick={()=>setPage(i+1)}>{i+1}</Button>)}
            <Button variant="outline" size="sm" className="h-7 px-2" disabled={page===totalPages} onClick={()=>setPage(p=>p+1)}><ChevronRight className="h-4 w-4"/></Button>
          </div>
        </div>
      )}

      {/* Brief Detail Modal */}
      {selectedBrief && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedBrief(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Brief Details</h3>
              <Button variant="ghost" size="sm" onClick={() => setSelectedBrief(null)}>✕</Button>
            </div>
            <div className="space-y-3 text-sm">
              {[
                ['Title', selectedBrief.title],
                ['Status', { draft: 'Taslak', published: 'Yayında', awarded: 'Tamamlandı', closed: 'Kapatıldı' }[selectedBrief.status] || selectedBrief.status],
                ['City', selectedBrief.city || '—'],
                ['Booth Size', selectedBrief.booth_size ? `${selectedBrief.booth_size}m` : '—'],
                ['Budget', `${selectedBrief.budget_min?.toLocaleString()} – ${selectedBrief.budget_max?.toLocaleString()} ${selectedBrief.currency}`],
                ['Deadline', selectedBrief.deadline ? new Date(selectedBrief.deadline).toLocaleDateString('en-GB') : '—'],
                ['Received Quotes', selectedBrief.proposals_count ?? 0],
              ].map(([label, value]) => (
                <div key={label} className="flex justify-between py-2 border-b border-gray-50">
                  <span className="text-gray-500">{label}</span>
                  <span className="text-gray-900 font-medium text-right max-w-[60%]">{String(value)}</span>
                </div>
              ))}
            </div>
            {selectedBrief.status === 'published' && (
              <Button className="w-full mt-4 gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                onClick={() => { setSelectedBrief(null); setInviteBriefId(selectedBrief.id); }}>
                <UserPlus className="h-4 w-4" />Invite Contractor to This Brief
              </Button>
            )}
            {selectedBrief.proposals_count > 0 && (
              <Button variant="outline" className="w-full mt-2 gap-2 border-orange-200 text-orange-600 hover:bg-orange-50"
                data-testid="view-brief-proposals-btn"
                onClick={() => { setSelectedBrief(null); navigate(`/app/exhibitor/proposals?brief=${selectedBrief.id}`); }}>
                <Send className="h-4 w-4" />View {selectedBrief.proposals_count} Proposals & Message
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Template Modal */}
      {showTemplates && (
        <TemplateModal token={token} onClose={() => setShowTemplates(false)}
          onSelect={(tmpl) => { setShowTemplates(false); navigate('/app/exhibitor/briefs/new', { state: { template: tmpl } }); }} />
      )}

      {/* Invite Contractor Modal */}
      {inviteBriefId && (
        <InviteContractorModal token={token} briefId={inviteBriefId} onClose={() => setInviteBriefId(null)} />
      )}

      {/* Brief Boost Modal */}
      {boostBriefId && (() => {
        const brief = briefs.find(b => b.id === boostBriefId);
        return brief ? (
          <BoostModal
            brief={brief}
            token={token}
            onClose={() => setBoostBriefId(null)}
          />
        ) : null;
      })()}
    </div>
  );
};

export default ExhibitorBriefs;
